import { ICloneable } from '../../../contracts/cloneable.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { Price } from '../price.js';
import { DayOfYear } from '../day-of-year.js';
import { Currency } from '../currency.js';
import { EventTicketOrder } from '../event-ticket-order.js';
import { OrderSource } from '../order-source.js';
import { TicketType } from '../ticket-type.js';
import { OrderDiscountDetails } from '../../../utility/statistics_discount_utils.js';
import { isNullOrUndefined } from '../../../utility/null-safety.js';
import { OnlineEventTicketOrder } from '../online-event-ticket-order.js';
import { SalesIncomeSummary } from './sales-income-summary.js';
import { EventTicketStatus } from '../event-ticket.js';
import { LegacyPaymentHelper } from '../../../payment/legacy-payment-helper.js';

export class DailySalesIncomeSummary implements IDataComparable, ICloneable {
  constructor(
    public readonly day: DayOfYear,
    public readonly sales: SalesIncomeSummary,
  ) {}

  static empty(currency: Currency, day: DayOfYear): DailySalesIncomeSummary {
    return new DailySalesIncomeSummary(
        day,
        SalesIncomeSummary.empty(currency),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DailySalesIncomeSummary {
    return new DailySalesIncomeSummary(
        context.fromData<DayOfYear>(MapUtils.get<SerializedData>(map, 'day')),
        SalesIncomeSummary.fromMap(context, MapUtils.get<SerializedData>(map, 'sales')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'day': context.toData(this.day),
      'sales': this.sales.toMap(context),
    };
  }

  clone(): this {
    return new DailySalesIncomeSummary(
        this.day.clone(),
        this.sales.clone(),
    ) as this;
  }

  isSame(other: DailySalesIncomeSummary): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.day, this.day) && isSameData(other.sales, this.sales);
  }

  addTicketOrder(order: EventTicketOrder, discountDetails: OrderDiscountDetails | null) {
    const currency = order.tickets[0].template.price.currency;
    const isOnline = order.source === OrderSource.Client;
    const isPromoter = order.tickets[0].ticketType === TicketType.Promoter;
    const isProtocol = order.tickets[0].ticketType === TicketType.Protocol;

    let liveOrScannedTickets : number = 0;
    let legacyOrderPrice : Price = Price.zero(currency);

    let orderPrice: Price | undefined | null = discountDetails?.orderDetails?.discountedPrice;
    orderPrice ??= LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true });

    for(const ticket of order.tickets) {
      if(ticket.status === EventTicketStatus.Live || ticket.status === EventTicketStatus.Scanned) {
        liveOrScannedTickets++;
        const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
        legacyOrderPrice = legacyOrderPrice.add(ticketPrice);
      }
    }

    orderPrice ??= legacyOrderPrice;

    if(isPromoter) {
      this.sales.promoterOrders++;
      this.sales.promoterTickets += liveOrScannedTickets;
      this.sales.promoterIncome = this.sales.promoterIncome.add(orderPrice);
    }
    else if(isProtocol) {
      this.sales.protocolOrders++;
      this.sales.protocolTickets += liveOrScannedTickets;
      this.sales.protocolIncome = this.sales.protocolIncome.add(orderPrice);
    }
    else if(isOnline) {
      this.sales.onlineOrders++;
      this.sales.onlineTickets += liveOrScannedTickets;
      this.sales.onlineIncome = this.sales.onlineIncome.add(orderPrice);
    }
    else {
      this.sales.offlineOrders++;
      this.sales.offlineTickets += liveOrScannedTickets;
      this.sales.offlineIncome = this.sales.offlineIncome.add(orderPrice);
    }

    this.sales.totalOrders++;
    this.sales.totalTickets += liveOrScannedTickets;
    this.sales.totalIncome = this.sales.totalIncome.add(orderPrice);
  }

  addOnlineTicketOrder(order: OnlineEventTicketOrder, discountDetails: OrderDiscountDetails | null) {
    const currency = order.tickets[0].template.price.currency;

    let orderPrice: Price | undefined | null = discountDetails?.orderDetails?.discountedPrice;
    orderPrice ??= LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true });

    if(isNullOrUndefined(orderPrice)) {
      orderPrice ??= Price.zero(currency);

      for(const ticket of order.tickets) {
        const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
        orderPrice = orderPrice?.add(ticketPrice);
      }
    }

    this.sales.onlineOrders++;
    this.sales.onlineTickets += order.tickets.length;
    this.sales.onlineIncome = this.sales.onlineIncome.add(orderPrice);

    this.sales.totalOrders++;
    this.sales.totalTickets += order.tickets.length;
    this.sales.totalIncome = this.sales.totalIncome.add(orderPrice);
  }

  // HACK: We can't be sure if a ticket was refunded along with the order unless we provide the original order.
  removeTicketOrder(order: EventTicketOrder, originalOrder: EventTicketOrder, discountDetails: OrderDiscountDetails | null) {
    const currency = order.tickets[0].template.price.currency;
    const isOnline = order.source === OrderSource.Client;
    const isPromoter = order.tickets[0].ticketType === TicketType.Promoter;
    const isProtocol = order.tickets[0].ticketType === TicketType.Protocol;

    let removedTickets : number = 0;
    let orderPrice: Price = Price.zero(currency);

    for(let i = 0; i < order.tickets.length; ++i) {
      if(originalOrder.tickets[i].status !== EventTicketStatus.Refunded) {
        const ticket = order.tickets[i];
        const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
        orderPrice = orderPrice.add(ticketPrice);
        removedTickets++;
      }
    }

    if(isPromoter) {
      this.sales.promoterOrders--;
      this.sales.promoterTickets -= removedTickets;
      this.sales.promoterIncome = this.sales.promoterIncome.difference(orderPrice);
    }
    else if(isProtocol) {
      this.sales.protocolOrders--;
      this.sales.protocolTickets -= removedTickets;
      this.sales.protocolIncome = this.sales.protocolIncome.difference(orderPrice);
    }
    else if(isOnline) {
      this.sales.onlineOrders--;
      this.sales.onlineTickets -= removedTickets;
      this.sales.onlineIncome = this.sales.onlineIncome.difference(orderPrice);
    }
    else {
      this.sales.offlineOrders--;
      this.sales.offlineTickets -= removedTickets;
      this.sales.offlineIncome = this.sales.offlineIncome.difference(orderPrice);
    }

    this.sales.totalOrders--;
    this.sales.totalTickets -= removedTickets;
    this.sales.totalIncome = this.sales.totalIncome.difference(orderPrice);

    this.sales.ensurePositive();
  }

  removeOnlineTicketOrder(order: OnlineEventTicketOrder, discountDetails: OrderDiscountDetails | null) {
    const currency = order.tickets[0].template.price.currency;

    let orderPrice: Price | undefined | null = discountDetails?.orderDetails?.discountedPrice;
    orderPrice ??= LegacyPaymentHelper.calculateIncomeForOrder(order, { applyProducts: false, applySupplements: true });

    if(isNullOrUndefined(orderPrice)) {
      orderPrice ??= Price.zero(currency);

      for(const ticket of order.tickets) {
        const ticketPrice = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice ?? ticket.template.price.clone();
        orderPrice = orderPrice?.add(ticketPrice);
      }
    }

    this.sales.onlineOrders--;
    this.sales.onlineTickets -= order.tickets.length;
    this.sales.onlineIncome = this.sales.onlineIncome.difference(orderPrice);

    this.sales.totalOrders--;
    this.sales.totalTickets -= order.tickets.length;
    this.sales.totalIncome = this.sales.totalIncome.difference(orderPrice);

    this.sales.ensurePositive();
  }

  removeEventTicket(order: EventTicketOrder, discountDetails: OrderDiscountDetails | null, index: number) {
    const isOnline = order.source === OrderSource.Client;
    const ticket = order.tickets[index];
    const isPromoter = ticket.ticketType === TicketType.Promoter;
    const isProtocol = ticket.ticketType === TicketType.Protocol;

    let price: Price | undefined | null = discountDetails?.ticketsDetails?.get(ticket.uniqueId)?.discountedPrice;
    price ??= LegacyPaymentHelper.calculateIncomeForEventTicket(ticket, { applySupplement: true });
    price ??= ticket.template.price.clone();

    if(isPromoter) {
      this.sales.promoterTickets--;
      this.sales.promoterIncome = this.sales.promoterIncome.difference(price);
    }
    else if(isProtocol) {
      this.sales.protocolTickets--;
      this.sales.protocolIncome = this.sales.protocolIncome.difference(price);
    }
    else if(isOnline) {
      this.sales.onlineTickets--;
      this.sales.onlineIncome = this.sales.onlineIncome.difference(price);
    }
    else {
      this.sales.offlineTickets--;
      this.sales.offlineIncome = this.sales.offlineIncome.difference(price);
    }

    this.sales.totalTickets--;
    this.sales.totalIncome = this.sales.totalIncome.difference(price);

    this.sales.ensurePositive();
  }
}

